import { Component } from "@/types/types"
import { cx } from "@/utils/cx"
import { tx } from "@/utils/tx"
import { Stack } from "@/ui/atoms/layout/Stack"
import { Title } from "@/ui/atoms/type/Title"
import { BodyText } from "@/ui/atoms/type/BodyText"
import { TextLink } from "@/ui/atoms/controls/TextLink"
import { Icon } from "@/ui/atoms/media/Icon"
import { TextButton } from "@/ui/atoms/controls/TextButton"
import { IconButton } from "@/ui/atoms/controls/IconButton"
import { logger } from "@/utils/logger"
import { useNotifications } from "@/hooks/_archive/global/notifications/useNotifications"

export const COMPONENT_NAME = "notificationCard"

export const presets = {
  variant: {
    info: {
      sidebarClassName: "bg-fill-accent",
      icon: "text-icon-accent",
    },
    success: {
      sidebarClassName: "bg-fill-success",
      icon: "text-icon-success",
    },
    warning: {
      sidebarClassName: "bg-fill-warning",
      icon: "text-icon-warning",
    },
    failure: {
      sidebarClassName: "bg-fill-critical",
      icon: "text-icon-critical",
    },
    ai: {
      sidebarClassName: "bg-ai",
      icon: "text-ai",
    },
  },
}

export interface Props extends Component {
  variant?: keyof typeof presets.variant
  id?: string
  title: string
  text: string
  links: {
    label: string
    href?: string
    onClick?: () => void
    newTab?: boolean
  }[]
  onClose?: () => void
}

export function NotificationCard({
  className,
  variant = "info",
  id,
  title,
  text,
  links,
  onClose,
  testId = COMPONENT_NAME,
}: Props) {
  const resolvedVariant = presets.variant[variant]
  const { icon, sidebarClassName } = resolvedVariant
  const [_, __, removeNotification] = useNotifications()

  const closable = typeof onClose === "function"

  function handleCloseButtonClick() {
    if (!id) return
    logger.debug("[Interaction] Notification card closed")
    onClose?.()
    removeNotification(id)
  }

  return (
    <article
      className={cx(`flex p-4 w-full rounded bg-surface relative text-left`, className)}
      key={id}
      data-test={testId}
    >
      {/* Close button */}
      {closable && (
        <IconButton
          onClick={handleCloseButtonClick}
          icon="closeSm"
          label="Dismiss notification"
          className="absolute top-1.5 right-1.5 text-icon-subtle"
        />
      )}

      {/* Sidebar */}
      <div
        className={cx(`absolute inset-0 right-auto w-1.5 shrink-0 rounded-l`, sidebarClassName)}
        data-test={tx(testId, "sidebar")}
      />

      <div className="flex gap-3">
        {/* Icon */}
        <div className={cx(`size-6 shrink-0 grid`, icon)}>
          {variant === "info" && <Icon name="campaignMd" />}
          {variant === "warning" && <Icon name="warningMd" />}
          {variant === "ai" && <Icon name="aIMd" />}
          {variant === "success" && <Icon name="checkCircleMd" />}
          {variant === "failure" && <Icon name="errorCircleMd" />}
        </div>

        <Stack className="gap-2.5">
          {/* Title */}
          <Title size="xs" className="line-clamp-2" testId={tx(testId, "title")}>
            {title}
          </Title>

          {/* Text */}
          {!!text && (
            <BodyText
              size="sm"
              className="line-clamp-4 text-text-secondary"
              testId={tx(testId, "text")}
            >
              {text}
            </BodyText>
          )}

          {/* Links */}
          {(!!links.length || closable) && (
            <div className="pt-1 flex gap-4">
              {links.map((link) => {
                if (link.href) {
                  return (
                    <TextLink
                      key={link.label}
                      href={link.href}
                      style="bold"
                      newTab={link.newTab}
                      className="text-text-subtle text-sm"
                      testId={tx(testId, "link")}
                    >
                      {link.label}
                    </TextLink>
                  )
                }

                if (typeof link.onClick === "function") {
                  return (
                    <TextButton
                      key={link.label}
                      onClick={link.onClick}
                      className="text-text-subtle text-sm"
                      testId={tx(testId, "link")}
                    >
                      {link.label}
                    </TextButton>
                  )
                }
              })}
              {closable && (
                <TextButton
                  onClick={handleCloseButtonClick}
                  className="text-text-subtle text-sm"
                  testId={tx(testId, "dismissLink")}
                >
                  Dismiss
                </TextButton>
              )}
            </div>
          )}
        </Stack>
      </div>
    </article>
  )
}
